import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { PageHeader, Breadcrumb } from '@/components/layout'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Skeleton,
  Alert,
} from '@/components/ui'
import { Receipt, ArrowLeft, RefreshCw, ExternalLink } from 'lucide-react'
import { useApiClient, api, type AuthorizedAppDetail, type Receipt as ReceiptType } from '@/api'

export function AppBillingPage() {
  const { appSlug } = useParams<{ appSlug: string }>()
  const client = useApiClient()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [app, setApp] = useState<AuthorizedAppDetail | null>(null)
  const [receipts, setReceipts] = useState<ReceiptType[]>([])

  const loadData = async () => {
    if (!appSlug) return
    setLoading(true)
    setError(null)
    try {
      const [appRes, receiptsRes] = await Promise.all([
        api.getApp(client, appSlug),
        api.getReceipts(client, 50, undefined, appSlug),
      ])
      setApp(appRes.app)
      setReceipts(receiptsRes.receipts)
    } catch (err) {
      console.error('Failed to load app billing data:', err)
      setError('Failed to load billing information')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [appSlug])

  if (loading) {
    return (
      <div>
        <PageHeader
          title="App Billing"
          description="Loading..."
          breadcrumb={
            <Breadcrumb
              items={[
                { label: 'Billing', href: '/billing' },
                { label: 'App' },
              ]}
            />
          }
        />
        <div className="space-y-6">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error || !app) {
    return (
      <div>
        <PageHeader
          title="App Billing"
          description="Manage billing for this app"
          breadcrumb={
            <Breadcrumb
              items={[
                { label: 'Billing', href: '/billing' },
                { label: 'App' },
              ]}
            />
          }
        />
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-[var(--text-secondary)] mb-4">
              {error || 'App not found'}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="secondary" asChild>
                <Link to="/billing">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Billing
                </Link>
              </Button>
              <Button onClick={loadData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const totalSpent = receipts.reduce((sum, r) => sum + r.amount_cents, 0)

  return (
    <div>
      <PageHeader
        title={`Billing · ${app.app.name}`}
        description="Billing history for this app"
        breadcrumb={
          <Breadcrumb
            items={[
              { label: 'Billing', href: '/billing' },
              { label: app.app.name },
            ]}
          />
        }
      />

      {/* Summary */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-[var(--bg-elevated-2)] flex items-center justify-center shrink-0">
              {app.app.logo_url ? (
                <img
                  src={app.app.logo_url}
                  alt={app.app.name}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <span className="text-lg font-medium text-[var(--text-secondary)]">
                  {app.app.name[0]}
                </span>
              )}
            </div>
            <div>
              <h3 className="text-lg font-medium text-[var(--text-primary)]">
                {app.app.name}
              </h3>
              <p className="text-sm text-[var(--text-secondary)]">
                {receipts.length} transaction{receipts.length !== 1 ? 's' : ''} •{' '}
                {formatCurrency(totalSpent, 'USD')} total
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          {receipts.length === 0 ? (
            <div className="text-center py-6">
              <Receipt className="h-8 w-8 text-[var(--text-muted)] mx-auto mb-3" />
              <p className="text-sm text-[var(--text-secondary)]">
                No transactions yet
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Your purchases from {app.app.name} will appear here
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border-subtle)]">
                    <th className="text-left text-xs font-medium text-[var(--text-secondary)] pb-3">
                      Receipt
                    </th>
                    <th className="text-left text-xs font-medium text-[var(--text-secondary)] pb-3">
                      Description
                    </th>
                    <th className="text-right text-xs font-medium text-[var(--text-secondary)] pb-3">
                      Amount
                    </th>
                    <th className="text-right text-xs font-medium text-[var(--text-secondary)] pb-3">
                      Date
                    </th>
                    <th className="text-right text-xs font-medium text-[var(--text-secondary)] pb-3">

                    </th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((receipt) => (
                    <tr
                      key={receipt.id}
                      className="border-b border-[var(--border-subtle)] last:border-0"
                    >
                      <td className="py-4 text-sm font-mono text-[var(--text-primary)]">
                        {receipt.receipt_number}
                      </td>
                      <td className="py-4 text-sm text-[var(--text-secondary)]">
                        {receipt.description || 'Charge'}
                      </td>
                      <td className="py-4 text-sm text-right font-medium text-[var(--text-primary)]">
                        {formatCurrency(receipt.amount_cents, receipt.currency)}
                      </td>
                      <td className="py-4 text-sm text-right text-[var(--text-secondary)]">
                        {formatDate(receipt.issued_at)}
                      </td>
                      <td className="py-4 text-right">
                        {receipt.pdf_url && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={receipt.pdf_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function formatCurrency(cents: number, currency: string): string {
  const amount = cents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount)
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  })
}
